import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUsers, FaLock, FaSearch, FaInfoCircle, FaSyncAlt, 
  FaTimes, FaSort, FaWifi, FaChartLine, FaSpinner
} from 'react-icons/fa';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { Helmet } from 'react-helmet-async';

// Lazy load Chart.js và các components liên quan để cải thiện hiệu năng
const LazyChart = lazy(() => import('../components/LazyChart'));

const Servers = () => {
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('players');
  const [selectedServer, setSelectedServer] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [dailyStats, setDailyStats] = useState([]);
  const [currentStats, setCurrentStats] = useState(null);
  const modalRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [showChart, setShowChart] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setSelectedServer(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchServers = async () => {
    try {
      setIsLoading(true);
      setIsRefreshing(true);
      
      const response = await fetch('/api/servers/list');
      
      if (!response.ok) {
        throw new Error('Không thể kết nối đến máy chủ');
      }
      
      const data = await response.json();
      setServers(data.servers);
      setFilteredServers(data.servers);
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error('Lỗi khi lấy dữ liệu máy chủ:', error);
      setError(error.message);
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const fetchPlayerStats = async () => {
    try {
      setStatsLoading(true);
      
      const dailyResponse = await fetch('/api/stats/daily?days=7');
      if (!dailyResponse.ok) {
        throw new Error('Không thể lấy thống kê người chơi theo ngày');
      }
      const dailyData = await dailyResponse.json();
      
      const currentResponse = await fetch('/api/stats/current');
      if (!currentResponse.ok) {
        throw new Error('Không thể lấy thống kê người chơi hiện tại');
      }
      const currentData = await currentResponse.json();
      
      setDailyStats(dailyData.data);
      setCurrentStats(currentData);
      setStatsLoading(false);
    } catch (error) {
      console.error('Error fetching player statistics:', error);
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
    fetchPlayerStats();
  }, []);

  useEffect(() => {
    if (servers.length === 0) return;
    
    let results = servers.filter(server => 
      server.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      server.info.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    switch(sortBy) {
      case 'players':
        results = [...results].sort((a, b) => b.players - a.players);
        break;
      case 'name':
        results = [...results].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'ping':
        results = [...results].sort((a, b) => a.ping - b.ping);
        break;
      default:
        break;
    }
    
    setFilteredServers(results);
  }, [searchTerm, sortBy, servers]);

  const getPlayerPercentage = (current, max) => {
    return (current / max) * 100;
  };

  const getPingStatus = (ping) => {
    if (ping < 50) return {
      text: 'Tốt',
      color: 'ping-good',
      class: 'green'
    };
    
    if (ping < 120) return {
      text: 'Trung bình',
      color: 'ping-medium',
      class: 'yellow'
    };
    
    return {
      text: 'Kém',
      color: 'ping-bad',
      class: 'red'
    };
  };

  const prepareChartData = () => {
    if (!dailyStats || dailyStats.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    let filteredStats = dailyStats;
    if (isMobile && dailyStats.length > 5) {
      filteredStats = [
        dailyStats[0], 
        dailyStats[Math.floor(dailyStats.length / 4)],
        dailyStats[Math.floor(dailyStats.length / 2)], 
        dailyStats[Math.floor(3 * dailyStats.length / 4)],
        dailyStats[dailyStats.length - 1]
      ];
    }
    
    const labels = filteredStats.map(stat => {
      const date = new Date(stat.date);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Người chơi cao nhất',
          data: filteredStats.map(stat => stat.max_players),
          fill: !isMobile,
          backgroundColor: 'rgba(74, 222, 174, 0.2)',
          borderColor: 'rgba(74, 222, 174, 1)',
          tension: isMobile ? 0 : 0.4,
          pointBackgroundColor: 'rgba(74, 222, 174, 1)',
          pointBorderColor: '#fff',
          pointRadius: isMobile ? 1 : 4,
          pointHoverRadius: isMobile ? 2 : 6,
          borderWidth: isMobile ? 1 : 2,
        },
        {
          label: 'Người chơi trung bình',
          data: filteredStats.map(stat => Math.round(stat.avg_players)),
          fill: false,
          borderColor: 'rgba(155, 89, 182, 1)',
          borderDash: isMobile ? [] : [5, 5],
          tension: isMobile ? 0 : 0.4,
          pointBackgroundColor: 'rgba(155, 89, 182, 1)',
          pointBorderColor: '#fff',
          pointRadius: isMobile ? 1 : 4,
          pointHoverRadius: isMobile ? 2 : 6,
          borderWidth: isMobile ? 1 : 2,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: isMobile ? 0 : 1000
    },
    hover: {
      animationDuration: isMobile ? 0 : 400
    },
    responsiveAnimationDuration: isMobile ? 0 : 500,
    elements: {
      line: {
        tension: isMobile ? 0 : 0.4
      },
      point: {
        radius: isMobile ? 1 : 4,
        hoverRadius: isMobile ? 2 : 6
      }
    },
    plugins: {
      legend: {
        display: !isMobile,
        labels: {
          color: 'rgba(255, 255, 255, 0.8)'
        }
      },
      tooltip: {
        enabled: !isMobile,
      }
    },
    scales: {
      x: {
        grid: {
          display: !isMobile,
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: isMobile ? 5 : 10
        }
      },
      y: {
        grid: {
          display: !isMobile,
          color: 'rgba(255, 255, 255, 0.1)'
        },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          maxTicksLimit: isMobile ? 4 : 8
        }
      }
    },
    events: isMobile ? [] : ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove']
  };

  return (
    <>
      <SEO 
        title="Danh sách máy chủ tại PSA Roleplay"
        description="Khám phá và tham gia các máy chủ của PSA Roleplay. Tìm kiếm máy chủ phù hợp với bạn."
      />
      
      <Helmet>
        <script type="application/ld+json">
        {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Danh sách máy chủ tại PSA Roleplay",
              "description": "Khám phá và tham gia các máy chủ của PSA Roleplay. Tìm kiếm máy chủ phù hợp với bạn.",
              "url": "https://psa.world/servers",
              "about": {
                "@type": "GameServer",
                "game": "GTA San Andreas Multiplayer",
                "name": "PSA Roleplay"
              }
        })}
        </script>

        <script type="application/ld+json">
        {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Trang chủ",
                "item": "https://psa.world"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Danh sách máy chủ tại PSA Roleplay",
                "item": "https://psa.world/news"
              }
            ]
        })}
        </script>
      </Helmet>
      
      <div className="min-h-screen pt-24 pb-16 bg-dark-purple-main relative overflow-hidden">
        <div className="page-servers-bg absolute inset-0 opacity-30"></div>
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-dark-purple to-dark-purple-main"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="text-center mb-12 mt-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              <span className="text-white neon-glow-white">DANH SÁCH MÁY CHỦ</span>
            </h1>
            
            <motion.div 
              className="w-24 h-1 bg-accent-teal mx-auto mb-6 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: 96 }}
              transition={{ duration: 0.5 }}
            />
            
            <p className="text-light/80 text-lg max-w-2xl mx-auto">
              Khám phá và tham gia các máy chủ của PSA
            </p>
          </motion.div>
          
          {!isMobile && (
            <>
              <motion.div 
                className="absolute top-40 left-5 w-64 h-64 bg-accent-teal/10 rounded-full filter blur-[120px] z-0"
                animate={{ opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 5, repeat: Infinity }}
              />
              
              <motion.div 
                className="absolute bottom-40 right-10 w-80 h-80 bg-pink-500/10 rounded-full filter blur-[150px] z-0"
                animate={{ opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 6, repeat: Infinity }}
              />
            </>
          )}
          
          <div className="container mx-auto px-4 py-8 relative z-10">
            <motion.div
              className="mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="glass-effect p-6 rounded-xl">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white flex items-center">
                    <FaChartLine className="text-accent-teal mr-2" />
                    Thống kê người chơi
                  </h2>
                  
                  {isMobile && (
                    <button 
                      onClick={() => setShowChart(!showChart)}
                      className="neon-btn neon-btn-blue py-2 px-3 rounded-lg flex items-center text-sm"
                    >
                      {showChart ? 'Ẩn biểu đồ' : 'Hiện biểu đồ'}
                    </button>
                  )}
                </div>
                
                {statsLoading ? (
                  <div className="flex justify-center items-center h-64">
                    <div className="neon-loader">
                      <div className="circle"></div>
                      <div className="circle"></div>
                    </div>
                  </div>
                ) : (
                  <>
                    {currentStats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="glass-effect p-4 text-center">
                          <div className="text-light/60 text-sm mb-1">Người chơi hiện tại</div>
                          <div className="text-2xl font-bold text-accent-teal">{currentStats.currentPlayers}</div>
                        </div>
                        
                        <div className="glass-effect p-4 text-center">
                          <div className="text-light/60 text-sm mb-1">Cao nhất hôm nay</div>
                          <div className="text-2xl font-bold text-white">{currentStats.maxPlayers}</div>
                        </div>
                        
                        <div className="glass-effect p-4 text-center">
                          <div className="text-light/60 text-sm mb-1">Thời điểm cao nhất</div>
                          <div className="text-2xl font-bold text-white">
                            {currentStats.peakTime ? currentStats.peakTime.substring(0, 5) : 'N/A'}
                          </div>
                        </div>
                        
                        <div className="glass-effect p-4 text-center">
                          <div className="text-light/60 text-sm mb-1">Trung bình hôm nay</div>
                          <div className="text-2xl font-bold text-white">
                            {Math.round(currentStats.avgPlayers)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {(!isMobile || showChart) && (
                      <div className="w-full h-64 md:h-80">
                        <Suspense fallback={
                          <div className="flex justify-center items-center h-full">
                            <FaSpinner className="animate-spin text-accent-teal text-2xl" />
                          </div>
                        }>
                          <LazyChart data={prepareChartData()} options={chartOptions} />
                        </Suspense>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
            
            <motion.div 
              className="glass-effect mb-10 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex-grow relative max-w-md">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-light/40">
                    <FaSearch />
                  </div>
                  <input
                    type="text"
                    placeholder="Tìm kiếm máy chủ..."
                    className="w-full py-3 pl-10 pr-4 bg-dark/40 text-white border border-white/10 focus:border-accent-teal/50 outline-none rounded-lg transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="flex gap-2">
                  <button 
                    onClick={() => setSortBy('players')} 
                    className={`neon-btn ${sortBy === 'players' ? 'neon-btn-teal' : 'neon-btn-blue'} py-2 px-4 rounded-lg flex items-center gap-2`}
                  >
                    <FaUsers />
                    <span className="hidden md:inline">Người chơi</span>
                  </button>
                  
                  <button 
                    onClick={() => setSortBy('ping')} 
                    className={`neon-btn ${sortBy === 'ping' ? 'neon-btn-teal' : 'neon-btn-blue'} py-2 px-4 rounded-lg flex items-center gap-2`}
                  >
                    <FaWifi />
                    <span className="hidden md:inline">Ping</span>
                  </button>
                  
                  <button 
                    onClick={() => setSortBy('name')} 
                    className={`neon-btn ${sortBy === 'name' ? 'neon-btn-teal' : 'neon-btn-blue'} py-2 px-4 rounded-lg flex items-center gap-2`}
                  >
                    <FaSort />
                    <span className="hidden md:inline">Tên</span>
                  </button>
                  
                  <button 
                    onClick={() => {
                      setIsRefreshing(true);
                      fetchServers();
                    }}
                    disabled={isRefreshing}
                    className="neon-btn neon-btn-pink py-2 px-4 rounded-lg flex items-center"
                  >
                    <FaSyncAlt className={`${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
              
              <p className="text-light/60 text-sm mt-4">
                {filteredServers.length} máy chủ · Cập nhật lúc {new Date().toLocaleTimeString()}
              </p>
            </motion.div>
            
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="neon-loader">
                  <div className="circle"></div>
                  <div className="circle"></div>
                </div>
              </div>
            ) : error ? (
              <motion.div 
                className="glass-effect p-8 text-center border-l-4 border-red-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <FaInfoCircle className="text-red-500 text-3xl mx-auto mb-4" />
                <p className="text-red-400 text-lg mb-4">{error}</p>
                <button
                  onClick={fetchServers}
                  className="neon-btn neon-btn-pink py-2 px-6 rounded-lg inline-flex items-center"
                >
                  <FaSyncAlt className="mr-2" /> Thử lại
                </button>
              </motion.div>
            ) : filteredServers.length === 0 ? (
              <motion.div 
                className="glass-effect p-8 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <FaSearch className="text-light/40 text-4xl mx-auto mb-4" />
                <p className="text-light/80 text-lg mb-4">Không tìm thấy máy chủ nào phù hợp</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="neon-btn neon-btn-teal py-2 px-6 rounded-lg"
                >
                  Xóa tìm kiếm
                </button>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <AnimatePresence>
                  {filteredServers.map((server, index) => (
                    <ServerCard
                      key={`${server.ip_address}:${server.port}`}
                      server={server}
                      onClick={() => setSelectedServer(server)}
                      getPlayerPercentage={getPlayerPercentage}
                      getPingStatus={getPingStatus}
                      delay={isMobile ? 0 : index * 0.1}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
          
          <AnimatePresence>
            {selectedServer && (
              <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div 
                  ref={modalRef}
                  className="w-full max-w-3xl neon-border glass-effect relative"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-6">
                      <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                        {selectedServer.password_status === 1 && (
                          <FaLock className="text-yellow-400" />
                        )}
                        <span>{selectedServer.name}</span>
                      </h3>
                      
                      <button 
                        onClick={() => setSelectedServer(null)}
                        className="text-light/60 hover:text-white p-2"
                      >
                        <FaTimes />
                      </button>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="text-accent-teal font-medium mb-2 flex items-center gap-2">
                        <FaInfoCircle /> Thông tin máy chủ
                      </h4>
                      <p className="text-light/80">{selectedServer.info}</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                      <div className="server-stat glass-effect p-4">
                        <h5 className="text-white/60 text-sm mb-3">Người chơi</h5>
                        <div className="text-xl font-bold text-white flex items-center gap-2 mb-3">
                          <FaUsers className="text-accent-teal" />
                          <span>{selectedServer.players}/{selectedServer.max_players}</span>
                        </div>
                        <div className="neon-progress-bg">
                          <div 
                            className={`neon-progress-fill ${getPingStatus(selectedServer.ping).class}`}
                            style={{width: `${getPlayerPercentage(selectedServer.players, selectedServer.max_players)}%`}}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="server-stat glass-effect p-4">
                        <h5 className="text-white/60 text-sm mb-3">Ping</h5>
                        <div className="text-xl font-bold flex items-center gap-2">
                          <FaWifi className={getPingStatus(selectedServer.ping).color} />
                          <span className={getPingStatus(selectedServer.ping).color}>
                            {selectedServer.ping} ms
                          </span>
                        </div>
                        <div className="mt-2">
                          <span className={`ping-status ${getPingStatus(selectedServer.ping).color}`}>
                            {getPingStatus(selectedServer.ping).text}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedServer.password_status === 1 && (
                      <div className="mb-6 bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                        <div className="flex items-start gap-3">
                          <FaLock className="text-yellow-400 mt-1" />
                          <div>
                            <p className="font-medium text-yellow-400">Máy chủ được bảo vệ bằng mật khẩu</p>
                            <p className="text-light/70 text-sm">Máy chủ này yêu cầu mật khẩu để kết nối.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-end gap-3">
                      <button 
                        onClick={() => setSelectedServer(null)}
                        className="neon-btn bg-dark/40 text-light/80 hover:text-white py-2.5 px-6 rounded-lg border border-white/10"
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

const ServerCard = ({ server, onClick, getPlayerPercentage, getPingStatus, delay }) => {
  return (
    <motion.div
      className="neon-card hover:transform hover:-translate-y-1 hover:shadow-lg transition-all cursor-pointer h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
      onClick={onClick}
    >
      <div className="h-1 w-full bg-dark">
        <motion.div 
          className={`h-full neon-progress-fill ${getPingStatus(server.ping).class}`}
          initial={{ width: 0 }}
          animate={{ width: `${getPlayerPercentage(server.players, server.max_players)}%` }}
          transition={{ duration: 0.5, delay: delay }}
        ></motion.div>
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-base font-bold text-white flex items-center truncate pr-2">
            {server.password_status === 1 && (
              <span className="mr-1 text-yellow-400 flex-shrink-0" title="Máy chủ có mật khẩu">
                <FaLock className="text-sm" />
              </span>
            )}
            <span className="truncate">{server.name}</span>
          </h3>
          
          <span className={`${getPingStatus(server.ping).color} flex items-center gap-1 flex-shrink-0 text-xs`}>
            <FaWifi />
            {server.ping}ms
          </span>
        </div>
        
        <p className="text-light/70 text-xs mb-3 line-clamp-2">{server.info}</p>
        
        <div className="flex items-center">
          <FaUsers className="text-accent-teal mr-1 text-sm" />
          <span className="text-white text-sm">{server.players}/{server.max_players}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default Servers;
